import { useMutation, useQuery, useQueryClient } from "react-query";
import { API_URL, APIError, APIResponse, get, post, postFormData } from "../api";
import { useAuth } from "../context/AuthContext";
import { ComplianceAddress } from "./useComplianceAddress";

export interface SavedAddress extends ComplianceAddress {
  id: string;
  version: number;
  is_current: boolean;
  legacy_text?: string | null;
  proof_uploaded_at?: string | null;
  proof_address_version?: number | null;
}

export function useSavedAddresses() {
  const { user, refetch } = useAuth();
  const client = useQueryClient();
  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });
  const query = useQuery<APIResponse<{ addresses: SavedAddress[]; limit: number; proof_upload_enabled?: boolean }>, APIError>({
    queryKey: ["saved-addresses", user?.id],
    queryFn: () => get(`${API_URL}/account/compliance/addresses`, { headers: headers() }),
    enabled: !!user?.id,
  });
  const refresh = async () => {
    await Promise.all([
      client.invalidateQueries("saved-addresses"), client.invalidateQueries("compliance-address"),
      client.invalidateQueries("kyc-requirements"), client.invalidateQueries("geo-resolve"), refetch(),
    ]);
  };
  const save = useMutation<APIResponse, APIError, ComplianceAddress & { id?: string; version?: number; make_current: boolean }>(
    data => post(`${API_URL}/account/compliance/addresses`, data, { headers: headers() }),
    { onSuccess: refresh }
  );
  const remove = useMutation<APIResponse, APIError, SavedAddress>(
    address => post(`${API_URL}/account/compliance/addresses/${encodeURIComponent(address.id)}/remove`,
      { version: address.version }, { headers: headers() }),
    { onSuccess: refresh }
  );
  const proof = useMutation<APIResponse, APIError, { address: SavedAddress; file: File; type: string }>(
    ({ address, file, type }) => {
      const data = new FormData();
      data.append('version', String(address.version)); data.append('type', type); data.append('document', file);
      return postFormData(`${API_URL}/account/compliance/addresses/${encodeURIComponent(address.id)}/proof`, data, { headers: headers() });
    }, { onSuccess: refresh }
  );
  return { addresses: query.data?.data?.addresses ?? [], proofEnabled: query.data?.data?.proof_upload_enabled === true, query, save, remove, proof };
}
